import { useState, useEffect } from "react";
import axios from "axios";
import { Users, Mail, Phone, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (img) => {
  if (!img) return null;
  return img.startsWith("http") ? img : `${BACKEND_URL}${img}`;
};

const ClubMembersPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const { isAdmin, getToken } = useAuth();

  const getAuthHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  const fetchMembers = async () => {
    try {
      const headers = getToken() ? { Authorization: `Bearer ${getToken()}` } : {};
      const res = await axios.get(`${API}/club-members`, { headers });
      setMembers(res.data);
    } catch (err) {
      console.error("Error fetching members:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleDelete = async (member) => {
    const label = member.name;
    if (!window.confirm(`Er du sikker på du vil slette ${label}?`)) return;
    try {
      if (member.source === "registered" || member.member_id?.startsWith("reg_")) {
        await axios.delete(`${API}/member-codes/${encodeURIComponent(member.member_id.replace("reg_", ""))}`, { headers: getAuthHeaders() });
      } else {
        await axios.delete(`${API}/club-members/${member.member_id}`, { headers: getAuthHeaders() });
      }
      toast.success(`${label} er slettet`);
      setSelectedId(null);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke slette");
    }
  };

  const selected = members.find(m => m.member_id === selectedId);

  return (
    <div className="min-h-screen" data-testid="club-members-page">
      <div className="fixed inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_fcd88aad-c020-4690-8915-1928b4488717/artifacts/r88czvrl_IMG_4049.png')` }} />
      <div className="fixed inset-0 bg-black/60" />

      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4 sm:px-8">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl font-['Oswald'] font-bold text-white drop-shadow-lg">MEDLEMMER</h1>
            <p className="text-base text-gray-400 mt-1">Mød folkene bag The Others</p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Henter medlemmer...</p>
            </div>
          ) : members.length === 0 ? (
            <div className="bg-black/40 backdrop-blur-sm rounded-xl border border-white/10 p-12 text-center">
              <Users className="w-14 h-14 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-1">Ingen medlemmer endnu</h3>
              <p className="text-gray-500 text-sm">Medlemmer vil blive vist her.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {members.map((member) => (
                <div
                  key={member.member_id}
                  onClick={() => setSelectedId(selectedId === member.member_id ? null : member.member_id)}
                  className="bg-black/40 backdrop-blur-sm rounded-xl border border-white/10 hover:border-[#00d4ff]/30 transition-all cursor-pointer overflow-hidden"
                  data-testid={`member-card-${member.member_id}`}
                >
                  <div className="flex items-center gap-4 p-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden flex-shrink-0 border-2 border-white/10">
                      {member.image ? (
                        <img src={resolveImg(member.image)} alt={member.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                          <span className="text-2xl font-bold text-gray-500">{(member.nickname || member.name).charAt(0)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-['Oswald'] font-bold text-white truncate">{member.nickname || member.name}</h3>
                      {member.nickname && <p className="text-gray-400 text-xs italic">Civilt kendt som: {member.name}</p>}
                      {member.title && <p className="text-[#00d4ff] text-sm font-medium">{member.title}</p>}
                      {member.email && (
                        <p className="text-gray-500 text-xs mt-1 truncate flex items-center gap-1">
                          <Mail className="w-3 h-3 flex-shrink-0" /> {member.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Full profile modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedId(null)} data-testid="member-profile-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "linear-gradient(135deg, #111114 0%, #09090b 100%)", border: "1px solid rgba(0,212,255,0.15)" }}
          >
            <button onClick={() => setSelectedId(null)} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-white/10 text-gray-400 hover:text-white transition-all" data-testid={`close-member-${selected.member_id}`}>
              <X className="w-5 h-5" />
            </button>

            <div className="w-full h-64 sm:h-80 bg-gradient-to-br from-gray-800 to-gray-900 relative overflow-hidden">
              {selected.image ? (
                <img src={resolveImg(selected.image)} alt={selected.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-8xl font-bold text-gray-700">{(selected.nickname || selected.name).charAt(0)}</span>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-5">
                <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white">{selected.nickname || selected.name}</h2>
                {selected.nickname && <p className="text-gray-300 text-sm italic">Civilt kendt som: {selected.name}</p>}
                {selected.title && <p className="text-[#00d4ff] font-semibold mt-1">{selected.title}</p>}
              </div>
            </div>

            <div className="p-6 space-y-4">
              {selected.short_description && (
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{selected.short_description}</p>
              )}
              {selected.extended_description && (
                <p className="text-gray-400 text-sm whitespace-pre-wrap">{selected.extended_description}</p>
              )}

              {(selected.email || selected.phone) && (
                <div className="pt-3 border-t border-white/10 space-y-3">
                  {selected.email && (
                    <a href={`mailto:${selected.email}`} className="flex items-center gap-3 text-gray-300 hover:text-[#00d4ff] transition-colors text-sm" data-testid={`member-email-${selected.member_id}`}>
                      <Mail className="w-4 h-4 flex-shrink-0" /> {selected.email}
                    </a>
                  )}
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`} className="flex items-center gap-3 text-gray-300 hover:text-[#00d4ff] transition-colors text-sm" data-testid={`member-phone-${selected.member_id}`}>
                      <Phone className="w-4 h-4 flex-shrink-0" /> {selected.phone}
                    </a>
                  )}
                </div>
              )}

              {isAdmin && (
                <div className="pt-3 border-t border-white/10">
                  <button
                    onClick={() => handleDelete(selected)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded border border-red-900/30 transition-all"
                    data-testid={`delete-public-member-${selected.member_id}`}
                  >
                    <Trash2 size={14} /> Slet medlem
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubMembersPage;
